<template>
  <div>
    <NuxtLayout
      name="default"
      :is-primary-navbar-sticky="false"
      apply-now-type="link"
      :apply-now-url="programGraduate.applyNowUrl"
      :rfi-anchor-id="pageData.rfiSectionId"
    >
      <!-- section-introduction -->
      <HeroDegreeAtlas
        id="program-intro"
        :title="programGraduate.alternateTitle!"
        title-level="h1"
        title-size="medium"
        :bg-image-source="programGraduate.degreeImage!"
        :alt-text="programGraduate.degreeImageAlt!"
        :next-start-date="programGraduate.nextStartDate!"
        :info-list="generateInfoList"
        link-text="Learn more"
        link-url="#program-detail"
        @on-link-click="scrollToDetail($event, programGraduate.alternateTitle!, 'program-detail')"
      >
        <template #aboveContent>
          <Alert v-if="checkDate()" show variant="info">
            <div class="d-flex flex-row align-items-center">
              <font-awesome-icon
                :icon="['fa', 'info-circle']"
                class="alert-icon"
              ></font-awesome-icon>
              <span class="fs-small fw-bold">
                {{ programGraduate.webinar?.message + " " }}
                <a
                  href="#webinar-info"
                  class="rds-link"
                  @click="scrollToDetail($event, programGraduate.webinar?.message!, 'webinar-info')"
                  >Learn more</a
                >.
              </span>
            </div>
          </Alert>
        </template>
        <template #text>
          <div v-html="programGraduate.shortDescription"></div>
        </template>
      </HeroDegreeAtlas>

      <!-- offline campus link -->
      <SectionOfflineCampusLink :link="programGraduate.campusLink!" />

      <!-- section-rfi -->
      <SectionRfiDegree
        id="program-rfi"
        :career="programGraduate.category?.title!"
        :program-id="programGraduate.id!"
        :program-name="programGraduate.title!"
      ></SectionRfiDegree>

      <!-- section-secondary-navbar -->
      <NavbarStickyAtlas :nav-items="navbarItems">
        <template #stuckMenu>
          <div class="d-flex justify-content-end">
            <ButtonApplyNow
              type="link"
              :link-url="programGraduate.applyNowUrl as string"
              event-region="main content"
              event-section="secondary navbar"
            ></ButtonApplyNow>

            <a
              class="btn btn-secondary fs-medium fw-bold ms-space-sm"
              href="#program-rfi"
              @click="smoothScroll"
            >
              Request Info
            </a>
          </div>
        </template>
      </NavbarStickyAtlas>

      <!-- section-detail -->
      <SectionDetail
        id="program-detail"
        :title="programGraduate.longDescriptionTitle!"
        :description="programGraduate.longDescription!"
      ></SectionDetail>

      <!-- section-custom-block -->
      <section
        v-if="programGraduate.customBlockTitle || programGraduate.diplomaTitle"
        class="pb-space-xl pb-lg-space-xxl"
      >
        <div class="container">
          <div class="row">
            <div class="col-12 offset-lg-1 col-lg-10">
              <CardInfoHorizontal
                v-if="programGraduate.customBlockTitle"
                border-variant="light-2"
                :bg-image-source="(programGraduate.customBlockImage || undefined) as string"
                :image-fit="programGraduate.customBlockImage ? 'cover' : 'contain'"
                :bg-image-alt="programGraduate.customBlockImageAlt || undefined"
                :display-label="false"
                :title="programGraduate.customBlockTitle"
                title-size="large"
                :display-cta="false"
              >
                <div
                  class="mb-space-sm mb-lg-space-md"
                  v-html="programGraduate.customBlockDescription"
                ></div>
              </CardInfoHorizontal>

              <!-- section-diploma-info -->
              <CardInfoHorizontal
                v-if="programGraduate.diplomaTitle"
                border-variant="light-2"
                bg-image-source="https://cms.asuonline.asu.edu/sites/default/files/image-section-diploma-info.jpg"
                image-fit="cover"
                bg-image-alt="ASU graduates in maroon caps and gowns holding diplomas outdoors."
                :display-label="false"
                :title="programGraduate.diplomaTitle"
                title-size="large"
                :display-cta="false"
                class="mt-space-md"
              >
                <div
                  class="mb-space-sm mb-lg-space-md"
                  v-html="programGraduate.diplomaDescription"
                ></div>
              </CardInfoHorizontal>
            </div>
          </div>
        </div>
      </section>

      <!-- section webinar info -->
      <SectionIntroFalcon
        v-if="checkDate()"
        id="webinar-info"
        :title="programGraduate.webinar!.title!"
        title-variant="light-1"
        section-background-variant="light-2"
        container-bg-color="dark-3"
        image-position="right"
        :image-source="programGraduate.webinar!.image!"
      >
        <template #body>
          <div class="text-light-1 mb-space-sm" v-html="programGraduate.webinar!.description"></div>
          <div class="mb-space-md">
            <!-- put webinar date and time here -->
            <ul class="text-light-1 rds-list rds-list-secondary">
              <li class="mb-space-xs">
                <span class="fw-bold">Date: </span
                >{{
                  utilityService.formatWebinarDate(programGraduate.webinar?.webinarDate!, "date")
                }}
              </li>
              <li>
                <span class="fw-bold">Time: </span
                >{{
                  utilityService.formatWebinarDate(programGraduate.webinar?.webinarDate!, "time")
                }}
                (Arizona time)
              </li>
            </ul>
          </div>
          <a
            :href="programGraduate.webinar?.link?.uri!"
            class="btn btn-sm btn-secondary px-space-xs py-space-xxs"
            @click="
              handleWebinarBtnClick(
                $event,
                programGraduate.webinar?.title!,
                programGraduate.webinar?.link as LinkType
              )
            "
          >
            {{ programGraduate.webinar?.link?.text }}
          </a>
        </template>
      </SectionIntroFalcon>

      <!-- section-video -->
      <SectionVideoModal
        v-if="programGraduate.videoTitle"
        :title="programGraduate.videoTitle"
        :video-source="programGraduate.videoUrl!"
        bg-image-source="https://cms.asuonline.asu.edu/sites/default/files/2020-06/Ignite-66_mobile.jpg"
        @modal-open="
          analyticsComposable.trackModalEvent(
            'onclick',
            'open',
            'click',
            'main content',
            'details',
            'watch video',
            'video'
          )
        "
        @modal-close="
          analyticsComposable.trackModalEvent(
            'onclick',
            'close',
            'click',
            'main content',
            'details',
            'youtube video',
            'video'
          )
        "
      ></SectionVideoModal>

      <!-- section-courses -->
      <SectionFeaturedCourses
        v-if="(programGraduate.cmsCourseItems as ParagraphFeaturedCourses[])?.length > 0"
        id="program-courses"
        :title="programGraduate.featuredCoursesTitle!"
        :description="programGraduate.featuredCoursesDescription!"
        :courses="programGraduate.cmsCourseItems as any"
      ></SectionFeaturedCourses>

      <!-- section-careers -->
      <SectionRelatedCareers
        v-if="displayCareers"
        id="program-careers"
        class="mb-lg-space-xxl"
        :title="programGraduate.relatedCareersTitle!"
        :description="programGraduate.relatedCareersDescription!"
        :image-source="programGraduate.relatedCareersImage!"
        :image-alt="programGraduate.relatedCareersImageAlt!"
        :careers="programGraduate.cmsCareerExamples as ParagraphRelatedCareersExamples[]"
      ></SectionRelatedCareers>

      <!-- section prestigious-faculty -->
      <section
        class="section-bg rds-lazy py-space-lg py-md-space-xxl"
        data-background-image="https://cms.asuonline.asu.edu/sites/default/files/2021-08/NetworkV02-01.png"
        aria-labelledby="prestigious-faculty-title"
      >
        <div class="container">
          <div class="row">
            <div class="col-12 col-lg-10 offset-lg-1">
              <CardInfoHorizontal
                :bg-image-source="(programGraduate.facultyImage || undefined) as string"
                :image-fit="programGraduate.facultyImage ? 'cover' : 'contain'"
                bg-image-alt="A person stands against a starry background, wearing a navy blue top and gray pants."
                image-position="left"
                title=""
                :display-label="false"
                :display-highlight="false"
                :display-cta="false"
              >
                <h3
                  id="prestigious-faculty-title"
                  class="mb-space-sm mb-lg-space-sm h3-large text-dark-3"
                  tabindex="0"
                >
                  {{ programGraduate.prestigiousFacultyTitle }}
                </h3>
                <div tabindex="0" v-html="programGraduate.prestigiousFacultyDescription"></div>
              </CardInfoHorizontal>
            </div>
          </div>
        </div>
      </section>

      <!-- section-admission-requirement -->
      <SectionCollapseItems
        id="program-admissions"
        :title="programGraduate.admissionRequirementTitle!"
        :description="programGraduate.admissionRequirementDescription"
        :collapse-items="programGraduate.admissionRequirementItems as any"
        bg-color="light-2"
        event-component="admission requirements"
      ></SectionCollapseItems>

      <!-- section-accolades -->
      <SectionProgramAccolades
        id="program-accolades"
        class="pt-0 pb-space-xxs px-lg-space-xxxl pt-lg-space-xxl mb-0"
        :title="programGraduate.accoladesTitle!"
        :description="programGraduate.accoladesDescription"
        :items="programGraduate.accoladeItems as any"
      ></SectionProgramAccolades>

      <!-- section-cost -->
      <SectionCost
        id="program-cost"
        class="pt-space-xxl"
        :title="pageData.sectionCost.title"
        :text="pageData.sectionCost.description"
        :program-category="programGraduate.category?.title!"
        :program-code="programGraduate.programCode!"
        :cost-override="programGraduate.costOverride!"
        :cost-academic-year="programGraduate.costAcademicYear!"
        :latest-academic-year="programGraduate.latestAcademicYear!"
        :fee-code-resident="programGraduate.resProgramFeeCode!"
        :fee-code-non-resident="programGraduate.noresProgramFeeCode!"
        :cost-collection="programGraduate.costCollection as any"
        degree-type="Graduate"
      ></SectionCost>

      <!-- section-faq -->
      <SectionCollapseItems
        v-if="
          programGraduate.faqTitle &&
          (programGraduate.faqItems as ParagraphAccordionText[])?.length > 0
        "
        id="program-faq"
        :title="programGraduate.faqTitle"
        :collapse-items="programGraduate.faqItems as any"
        event-component="faq"
      ></SectionCollapseItems>

      <!-- section-interested-programs -->
      <SectionInterestedPrograms
        :degrees="programGraduate.interestedPrograms as DegreeSeekingProgram[]"
      ></SectionInterestedPrograms>
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
import Alert from "@/components/AlertMessage.vue";
import SectionCollapseItems from "@/components/SectionCollapseItems.vue";
import HeroDegreeAtlas from "@/components/degree-page/HeroDegreeAtlas.vue";
import SectionCost from "@/components/degree-page/SectionCost.vue";
import SectionDetail from "@/components/degree-page/SectionDetail.vue";
import SectionFeaturedCourses from "@/components/degree-page/SectionFeaturedCourses.vue";
import SectionInterestedPrograms from "@/components/degree-page/SectionInterestedPrograms.vue";
import SectionProgramAccolades from "@/components/degree-page/SectionProgramAccolades.vue";
import SectionRelatedCareers from "@/components/degree-page/SectionRelatedCareers.vue";
import ButtonApplyNow from "@/components/nav/ButtonApplyNow.vue";
import SectionRfiDegree from "@/components/rfi/SectionRfiDegree.vue";
import type {
  DegreeSeekingProgram,
  ParagraphAccordionText,
  ParagraphFeaturedCourses,
  ParagraphRelatedCareersExamples,
  ProgramGraduate
} from "@/server/resolvers/resolvers-types";
import type { ProgramGraduateBySlugQuery } from "@/types/graphql-operations";
import { scrollToTarget } from "@/utils";
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { SectionVideoModal } from "@rds-vue-ui/section-video-modal";
import { useWindowSize } from "@vueuse/core";
import SectionOfflineCampusLink from "~/components/degree-page/SectionOfflineCampusLink.vue";

const route = useRoute();

const data = await usePageQuery<ProgramGraduateBySlugQuery>(
  /* GraphQL */ `
    #graphql
    query ProgramGraduateBySlug($slug: String!) {
      programGraduate(slug: $slug) {
        id
        detailPage
        code
        programCode
        title
        alternateTitle
        shortDescription
        longDescriptionTitle
        longDescription
        weeksPerClass
        totalClasses
        totalCreditHours
        nextStartDate
        applyNowUrl
        metaTagTitle
        metaTagDescription
        prestigiousFacultyTitle
        prestigiousFacultyDescription
        facultyImage
        videoTitle
        videoUrl
        degreeImage
        degreeImageAlt
        category {
          title
          slug
        }
        campusLink
        featuredCoursesTitle
        featuredCoursesDescription
        cmsCourseItems {
          title
          category
          description
          youtubeVideoId
          youtubeVideoDuration
          youtubeVideoUploadDate
        }
        relatedCareersTitle
        relatedCareersDescription
        relatedCareersImage
        relatedCareersImageAlt
        cmsCareerExamples {
          title
          description
        }
        admissionRequirementTitle
        admissionRequirementDescription
        admissionRequirementItems {
          title
          description
        }
        accoladesTitle
        accoladesDescription
        accoladeItems {
          title
          description
        }
        faqTitle
        faqDescription
        faqItems {
          title
          description
        }
        customBlockTitle
        customBlockDescription
        customBlockImage
        customBlockImageAlt
        costOverride
        costAcademicYear
        latestAcademicYear
        resProgramFeeCode
        noresProgramFeeCode
        costCollection {
          amount
          summary
        }
        interestedPrograms {
          id
          title
          nextStartDate
          category {
            title
          }
          detailPage
          degreeImage
        }
        diplomaTitle
        diplomaDescription
        webinar {
          title
          message
          image
          description
          webinarDate
          link {
            text
            target
            uri
          }
        }
      }
    }
  `,
  {
    slug: route.params.slug as string
  },
  {
    key: (route.params.slug as string) + "-grad-data"
  }
);

if (!data?.programGraduate) {
  throw createError({
    message: `${String(useRoute().name)} query failed`
  });
}
const programGraduate = data.programGraduate;

const config = useRuntimeConfig();
const siteUrl = config.public.siteUrl;
const utilityService = useUtilityService();
const schemaService = useSchemaService();
const { width } = useWindowSize();
try {
  const weeksPerClass = programGraduate?.weeksPerClass;
  const repeatCount = isNaN(Number(weeksPerClass)) ? 7.5 : Number(weeksPerClass);
  const JSONLdSchema = schemaService.generateProgramTemplateSchema(
    `${siteUrl}${programGraduate.detailPage}`,
    // generateProgramTemplateSchema's `program` parameter is typed against the
    // full resolver schema regardless of which fields the function body
    // actually reads (a strict subset: alternateTitle, shortDescription,
    // longDescription, degreeImage, featuredCoursesDescription,
    // faqItems{title,description}, patResponse.programs{...},
    // bammProgram.cipCode -- see composables/useSchemaService.ts), so a
    // query-narrowed type can never structurally satisfy it. `unknown` hops
    // past that mismatch while still asserting the real schema shape, so a
    // future signature change that a query-narrowed type genuinely can't
    // satisfy still errors here instead of compiling silently.
    programGraduate as unknown as ProgramGraduate,
    programGraduate?.cmsCourseItems && programGraduate.cmsCourseItems !== undefined
      ? (programGraduate.cmsCourseItems as ParagraphFeaturedCourses[])
      : [],
    "graduate",
    programGraduate.totalCreditHours?.toString(),
    repeatCount
  );
  useHead({
    title: programGraduate.metaTagTitle,
    meta: [
      { name: "Title", content: programGraduate.metaTagTitle },
      {
        name: "Description",
        content: programGraduate.metaTagDescription
      },
      ...utilityService.generateSocialTags(
        programGraduate.metaTagTitle!,
        programGraduate.metaTagDescription!,
        programGraduate.degreeImage!
      )
    ],
    link: [
      {
        rel: "canonical",
        href: `${siteUrl}${programGraduate.detailPage}`
      }
    ],
    script: [JSONLdSchema]
  });
} catch (error) {
  console.error("=== Error in graduate slug");
  console.error(error);
}
definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"]
  }
});

const pageData = reactive({
  rfiSectionId: "program-rfi",
  sectionCost: {
    title: "Tuition calculator",
    description:
      '<p>Use our calculator to estimate your full-time or part-time tuition fees for this program prior to any financial aid. Keep in mind that most of our students receive financial aid, which can reduce out-of-pocket costs. <a href="/what-it-costs/financial-aid/" class="non-rds-link">Learn more.</a></p>'
  }
});

const displayCareers = computed(() => {
  return (
    programGraduate.cmsCareerExamples &&
    programGraduate.cmsCareerExamples.length > 0 &&
    programGraduate.relatedCareersImage
  );
});

const navbarItems = computed(() => {
  interface NavItemsContent {
    [key: string]: string;
  }

  const navbarItems: NavItemsContent = {};

  navbarItems["program-intro"] = "Overview";
  navbarItems["program-detail"] = "Details";

  if (
    programGraduate &&
    programGraduate?.cmsCourseItems &&
    programGraduate.cmsCourseItems?.length > 0
  ) {
    navbarItems["program-courses"] = "Courses";
  }

  if (displayCareers.value) {
    navbarItems["program-careers"] = "Careers";
  }

  navbarItems["program-admissions"] = "Admissions";
  navbarItems["program-accolades"] = "Accolades";
  navbarItems["program-cost"] = "Tuition";

  return navbarItems;
});
const generateInfoList = computed(() => {
  return [
    {
      value: programGraduate.totalClasses!,
      description: "Total classes: "
    },
    {
      value: programGraduate.weeksPerClass!,
      description: "Weeks per class: "
    },
    {
      value: programGraduate.totalCreditHours!,
      description: "Total credit hours: "
    }
  ];
});
const scrollToDetail = (e: Event, section: string, target: string) => {
  e.preventDefault();

  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    section.toLowerCase(),
    "learn more"
  );

  let navbarOffset = 59;
  if (width.value < 992) {
    navbarOffset = 65;
  }

  scrollToTarget(target, { offset: navbarOffset });
};
const smoothScroll = (e: Event) => {
  e.preventDefault();
  const eTarget = e.target as HTMLLinkElement;
  const eventText = eTarget.textContent ? eTarget.textContent.toLowerCase() : "N/A";
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    "secondary navbar",
    eventText,
    "secondary navbar"
  );
  let navbarOffset = 59;
  if (width.value < 992) {
    navbarOffset = 65;
  }
  const href = eTarget.getAttribute("href");

  scrollToTarget(href?.slice(1), { offset: navbarOffset });
};
const checkDate = () => {
  if (programGraduate!.webinar) {
    const currentTime = new Date(new Date().toDateString()).getTime();
    const webinarDate = new Date(
      new Date(programGraduate!.webinar.webinarDate!).toDateString()
    ).getTime();
    const checkTime = webinarDate - currentTime >= 0;
    return checkTime;
  }
  return false;
};
interface LinkType {
  text: string;
  uri: string;
}
const handleWebinarBtnClick = async (e: Event, section: string, link: LinkType) => {
  e.preventDefault();

  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    section.toLowerCase(),
    link.text.toLowerCase()
  );

  await navigateTo(link.uri, {
    open: {
      target: "_blank"
    }
  });
};
</script>

<style scoped lang="scss">
.section-bg {
  background-size: auto;
  background-position: top;
  background-repeat: repeat;
}
.alert-icon {
  font-size: 2rem;
  margin-right: 24px;
}

:deep(.bg-backdrop) {
  margin-top: 0px;
}
</style>
